/** Formatage francophone, centralisé pour que l'affichage reste cohérent. */

const NBSP = ' ';

export const number = (value: number | null | undefined): string =>
  value == null ? '—' : Math.round(value).toLocaleString('fr-FR');

export const weight = (value: number | null | undefined, digits = 1): string =>
  value == null ? '—' : value.toFixed(digits).replace('.', ',');

/**
 * Pesée telle que saisie : au centième si elle en a un, au dixième sinon.
 * `weight()` convient aux moyennes, dont le centième n'est que du bruit, mais
 * afficherait 75,45 comme 75,5 — et, dans un champ éditable, le réécrirait
 * ainsi au prochain enregistrement.
 */
export function measuredWeight(value: number | null | undefined): string {
  if (value == null) return '—';
  const tenths = value.toFixed(1);
  return (Number(tenths) === value ? tenths : value.toFixed(2)).replace('.', ',');
}

/** Toujours signé : un delta sans signe se lit mal dans un tableau. */
export function delta(value: number | null | undefined, digits = 1): string {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(digits).replace('.', ',')}`;
}

/** Fraction rendue en pourcentage signé : 0,007 → « +0,70 % ». */
export function signedPercent(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—';
  return `${delta(value * 100, digits)}${NBSP}%`;
}

export function signedInteger(value: number | null | undefined): string {
  if (value == null) return '—';
  const rounded = Math.round(value);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${Math.abs(rounded).toLocaleString('fr-FR')}`;
}

export const date = (value: Date, options: Intl.DateTimeFormatOptions): string =>
  value.toLocaleDateString('fr-FR', options);

export const dayMonth = (value: Date): string => date(value, { day: 'numeric', month: 'short' });

export const fullDate = (value: Date): string =>
  date(value, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export const monthYear = (value: Date): string => date(value, { month: 'long', year: 'numeric' });

/**
 * « il y a N j », ou « aujourd'hui » / « hier » quand c'est plus naturel. Un
 * décalage négatif désigne une journée à venir : la saisie anticipée en ouvre
 * une, et « il y a −1 j » ne se lit pas.
 */
export function relativeDays(offset: number): string {
  if (offset === 0) return "aujourd'hui";
  if (offset === 1) return 'hier';
  if (offset === -1) return 'demain';
  if (offset < 0) return `dans ${-offset}${NBSP}j`;
  return `il y a ${offset}${NBSP}j`;
}

/** Intervalle compact : « 8–14 sept. » si même mois, sinon « 28/08–03/09 ». */
export function range(from: Date, to: Date): string {
  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()}–${dayMonth(to)}`;
  }
  const short = (value: Date) => date(value, { day: '2-digit', month: '2-digit' });
  return `${short(from)}–${short(to)}`;
}

/**
 * Intervalle en toutes lettres, pour un titre : « 12–18 octobre 2026 », puis
 * « 28 sept. – 4 oct. 2026 » à cheval sur deux mois. L'année n'est écrite
 * qu'une fois, sauf quand elle change en route.
 */
export function spelledRange(from: Date, to: Date): string {
  const withYear = (value: Date) => date(value, { day: 'numeric', month: 'short', year: 'numeric' });
  if (from.getFullYear() !== to.getFullYear()) return `${withYear(from)} – ${withYear(to)}`;
  if (from.getMonth() !== to.getMonth()) return `${dayMonth(from)} – ${withYear(to)}`;
  return `${from.getDate()}–${date(to, { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

export const initials = (name: string | null, fallback: string): string =>
  (name ?? fallback)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';
